// src/components/common/SubscriptionPage.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Check, Zap, BookOpen, FlaskConical, Building2,
  Smartphone, Loader2, CheckCircle2, XCircle, Clock,
  ChevronRight, Receipt, RefreshCw, AlertCircle, Navigation,
  Shuffle, MapPin, Lock, Search, CheckSquare, Square,
  CreditCard, User, Star, ArrowRight, Shield,
} from 'lucide-react';
import { initiatePayment, getPaymentStatus, getMyPayments, setupBasicStations } from '../../services/paymentService';
import { fetchWeatherDevices } from '../../services/weatherService';

// ─── Tier definitions ─────────────────────────────────────────────────────────
const TIERS = [
  {
    key:      'basic',
    label:    'Basic',
    price:    0,
    badge:    'Free',
    icon:     Zap,
    color:    '#6b7280',
    bg:       'from-gray-500 to-gray-600',
    soft:     '#f9fafb',
    border:   '#e5e7eb',
    features: [
      'Up to 3 nearest stations',
      'Current readings only',
      '7-day chart history',
      'No data export',
    ],
  },
  {
    key:      'education',
    label:    'Education',
    price:    500,
    badge:    'Schools & Students',
    icon:     BookOpen,
    color:    '#2563eb',
    bg:       'from-blue-500 to-blue-700',
    soft:     '#eff6ff',
    border:   '#bfdbfe',
    features: [
      'Assigned school station + 5 nearby',
      '30-day history',
      'CSV export for academic use',
      'School dashboard',
    ],
  },
  {
    key:      'research',
    label:    'Research',
    price:    2500,
    badge:    'NGOs & Researchers',
    icon:     FlaskConical,
    color:    '#7c3aed',
    bg:       'from-violet-500 to-violet-700',
    soft:     '#f5f3ff',
    border:   '#c4b5fd',
    popular:  true,
    features: [
      'All 28 stations',
      '1-year history',
      'CSV & JSON export',
      'API access (rate-limited)',
    ],
  },
  {
    key:      'enterprise',
    label:    'Enterprise',
    price:    8000,
    badge:    'Organizations',
    icon:     Building2,
    color:    '#059669',
    bg:       'from-emerald-500 to-emerald-700',
    soft:     '#ecfdf5',
    border:   '#6ee7b7',
    features: [
      'All stations, unlimited history',
      'Priority support',
      'Full API access',
      'Custom integrations',
    ],
  },
];

// ─── Haversine ────────────────────────────────────────────────────────────────
const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371, toR = d => d * Math.PI / 180;
  const dLat = toR(lat2 - lat1), dLon = toR(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toR(lat1)) * Math.cos(toR(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ─── Basic Station Picker Modal ───────────────────────────────────────────────
const BasicSetupModal = ({ onClose, onSuccess }) => {
  const [phase,    setPhase]    = useState('choice');
  const [stations, setStations] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState([]);
  const [search,   setSearch]   = useState('');
  const [error,    setError]    = useState('');
  const [userLoc,  setUserLoc]  = useState(null);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      pos => setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  }, []);

  const stationsSorted = userLoc
    ? [...stations].map(s => ({
        ...s,
        _dist: s.lat != null && s.lng != null
          ? haversineKm(userLoc.lat, userLoc.lng, s.lat, s.lng) : Infinity,
      })).sort((a, b) => a._dist - b._dist)
    : stations;

  const nearestId = stationsSorted.length > 0 && stationsSorted[0]._dist !== Infinity
    ? (stationsSorted[0].id || stationsSorted[0].stationId) : null;

  const loadStations = async () => {
    setLoading(true);
    try { setStations(await fetchWeatherDevices()); }
    catch { setError('Failed to load stations. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleRandom = async () => {
    setLoading(true);
    try {
      const devs = stations.length ? stations : await fetchWeatherDevices().then(d => { setStations(d); return d; });
      let pick;
      if (userLoc) {
        const sorted = [...devs].map(s => ({ ...s, _dist: s.lat != null ? haversineKm(userLoc.lat, userLoc.lng, s.lat, s.lng) : Infinity })).sort((a, b) => a._dist - b._dist);
        const [first, ...rest] = sorted;
        pick = [first, ...[...rest].sort(() => Math.random() - 0.5).slice(0, 2)];
      } else {
        pick = [...devs].sort(() => Math.random() - 0.5).slice(0, 3);
      }
      setSelected(pick.map(d => d.id || d.stationId));
      setPhase('confirm');
    } catch { setError('Failed to load stations.'); }
    finally { setLoading(false); }
  };

  const handleManual = async () => {
    await loadStations();
    if (nearestId) setSelected([nearestId]);
    setPhase('pick');
  };

  const toggleStation = (id) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 3 ? [...prev, id] : prev);

  const handleConfirm = async () => {
    if (selected.length === 0) { setError('Select at least 1 station.'); return; }
    setPhase('saving');
    try { onSuccess(await setupBasicStations(selected)); }
    catch (e) { setError(e.message); setPhase('pick'); }
  };

  const filtered = stationsSorted.filter(s =>
    !search || (s.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.location || '').toLowerCase().includes(search.toLowerCase())
  );
  const selectedStations = stations.filter(s => selected.includes(s.id || s.stationId));

  return (
    <div className="fixed inset-0 bg-black/55 z-[1000] flex items-center justify-center p-5">
      <div className="bg-white rounded-2xl w-full max-w-[480px] max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="bg-gradient-to-r from-gray-600 to-gray-700 p-5 flex-shrink-0">
          <p className="text-white/70 text-[11px] font-bold uppercase tracking-widest m-0">Basic Plan · Free</p>
          <p className="text-white text-lg font-black mt-1 mb-0">Choose Your 3 Stations</p>
          <p className="text-white/70 text-xs mt-1 flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3 inline" />
            This selection is <strong className="text-amber-300">permanent</strong> on the free plan.
          </p>
        </div>

        <div className="p-5 flex-1 overflow-y-auto">
          {phase === 'choice' && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-gray-500 m-0">How would you like to receive your 3 stations?</p>
              <button onClick={handleRandom} disabled={loading} className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl bg-white hover:border-green-300 hover:bg-green-50 transition-all text-left disabled:opacity-60">
                {loading ? <Loader2 className="w-7 h-7 text-gray-400 animate-spin flex-shrink-0" /> :
                  <div className="w-11 h-11 rounded-xl bg-green-50 border border-green-200 flex items-center justify-center flex-shrink-0"><Shuffle className="w-5 h-5 text-green-600" /></div>}
                <div>
                  <p className="text-sm font-bold text-gray-900 m-0">Assign me automatically</p>
                  <p className="text-xs text-gray-500 mt-0.5 m-0">{nearestId ? 'Picks your nearest station + 2 others' : 'System picks 3 stations for you instantly'}</p>
                </div>
              </button>
              <button onClick={handleManual} disabled={loading} className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl bg-white hover:border-blue-300 hover:bg-blue-50 transition-all text-left disabled:opacity-60">
                <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-sm font-bold text-gray-900 m-0">Let me choose</p>
                  <p className="text-xs text-gray-500 mt-0.5 m-0">Browse available stations and pick up to 3</p>
                </div>
              </button>
              {error && <p className="text-red-500 text-xs text-center">{error}</p>}
              <button onClick={onClose} className="mt-1 p-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-700 text-sm font-bold cursor-pointer hover:bg-gray-100 transition-colors">Cancel</button>
            </div>
          )}

          {phase === 'pick' && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-800 font-bold m-0">Select up to 3 stations</p>
                <span className={`text-xs font-bold ${selected.length === 3 ? 'text-green-600' : 'text-gray-500'}`}>{selected.length}/3 selected</span>
              </div>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search stations…"
                  className="w-full pl-9 pr-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl outline-none focus:border-blue-400" />
              </div>
              <div className="max-h-72 overflow-y-auto border-2 border-gray-100 rounded-xl overflow-hidden">
                {loading ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 text-gray-400 animate-spin mx-auto" /></div>
                  : filtered.map(s => {
                    const id = s.id || s.stationId;
                    const isSel = selected.includes(id);
                    const isDisabled = !isSel && selected.length >= 3;
                    const isNearest = id === nearestId;
                    return (
                      <div key={id} onClick={() => !isDisabled && toggleStation(id)}
                        className={`flex items-center gap-3 px-4 py-2.5 border-b border-gray-50 transition-colors ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${isSel ? 'bg-green-50' : isNearest ? 'bg-blue-50/40' : 'bg-white hover:bg-gray-50'}`}>
                        {isSel ? <CheckSquare className="w-4 h-4 text-green-600 flex-shrink-0" /> : <Square className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-bold text-gray-900 m-0 truncate">{s.name || `Station ${id?.slice(-6)}`}</p>
                            {isNearest && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1 whitespace-nowrap"><Navigation className="w-2 h-2" />Nearest</span>}
                          </div>
                          <p className="text-xs text-gray-500 m-0">
                            {s.location || ''}
                            {s._dist != null && s._dist !== Infinity && <span className="text-green-600 font-semibold ml-1">{s._dist < 10 ? s._dist.toFixed(1) : Math.round(s._dist)} km</span>}
                          </p>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${s.status === 'online' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{s.status || 'unknown'}</span>
                      </div>
                    );
                  })}
              </div>
              {error && <p className="text-red-500 text-xs">{error}</p>}
              <div className="flex gap-2.5">
                <button onClick={() => { setPhase('choice'); setSelected([]); setSearch(''); }} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-700 text-sm font-bold cursor-pointer hover:bg-gray-100">Back</button>
                <button onClick={() => { if (!selected.length) { setError('Select at least 1 station.'); return; } setPhase('confirm'); }} disabled={!selected.length}
                  className="flex-[2] py-2.5 rounded-xl border-0 bg-gray-600 text-white text-sm font-bold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors">
                  Continue ({selected.length} selected)
                </button>
              </div>
            </div>
          )}

          {phase === 'confirm' && (
            <div className="flex flex-col gap-4">
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex gap-3">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 m-0 leading-relaxed"><strong>This selection is permanent</strong> on the free plan. Upgrade to change stations.</p>
              </div>
              <p className="text-sm font-bold text-gray-700 m-0">Your selected stations:</p>
              <div className="border-2 border-gray-100 rounded-xl overflow-hidden">
                {(selectedStations.length > 0 ? selectedStations : selected.map(id => ({ id, name: `Station ···${id.slice(-6)}` }))).map((s, i, arr) => (
                  <div key={s.id || s.stationId} className={`flex items-center gap-3 p-4 bg-green-50 ${i < arr.length - 1 ? 'border-b border-green-100' : ''}`}>
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0"><MapPin className="w-3.5 h-3.5 text-green-700" /></div>
                    <div>
                      <p className="text-sm font-bold text-gray-900 m-0">{s.name || `Station ${(s.id || s.stationId)?.slice(-6)}`}</p>
                      {s.location && <p className="text-xs text-gray-500 m-0">{s.location}</p>}
                    </div>
                  </div>
                ))}
              </div>
              {error && <p className="text-red-500 text-xs">{error}</p>}
              <div className="flex gap-2.5">
                <button onClick={() => setPhase(stations.length ? 'pick' : 'choice')} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-700 text-sm font-bold cursor-pointer hover:bg-gray-100">Back</button>
                <button onClick={handleConfirm} className="flex-[2] py-2.5 rounded-xl border-0 bg-emerald-600 text-white text-sm font-bold cursor-pointer flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors">
                  <Lock className="w-3.5 h-3.5" /> Confirm & Lock Selection
                </button>
              </div>
            </div>
          )}

          {phase === 'saving' && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-4" />
              <p className="text-sm font-bold text-gray-700 m-0">Saving your selection…</p>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

// ─── M-Pesa Payment Modal ─────────────────────────────────────────────────────
const PaymentModal = ({ tier, onClose, onSuccess }) => {
  const [phase, setPhase]     = useState('phone');
  const [phone, setPhone]     = useState('');
  const [error, setError]     = useState('');
  const [receipt, setReceipt] = useState('');
  const [, setCheckoutId]     = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPolling(), []);

  const startPolling = (id) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await getPaymentStatus(id);
        if (res.status === 'completed') { stopPolling(); setReceipt(res.mpesaReceiptNumber || ''); setPhase('success'); if (res.userData) onSuccess(res.userData); }
        else if (res.status === 'failed' || res.status === 'cancelled') { stopPolling(); setError(res.status === 'cancelled' ? 'Payment was cancelled on your phone.' : 'Payment failed. Please try again.'); setPhase('failed'); }
      } catch { /* ignore */ }
      if (attempts >= 30) { stopPolling(); setError('Payment timed out. If you paid, please contact support.'); setPhase('failed'); }
    }, 5000);
  };

  const handleSubmit = async () => {
    setError('');
    const clean = phone.replace(/\s+/g, '');
    if (!/^(07|01|\+2547|\+2541|2547|2541)\d{8}$/.test(clean)) { setError('Enter a valid Safaricom number e.g. 0712 345 678'); return; }
    try {
      setPhase('waiting');
      const res = await initiatePayment({ tier: tier.key, phone: clean });
      setCheckoutId(res.checkoutRequestId);
      startPolling(res.checkoutRequestId);
    } catch (e) { setError(e.message); setPhase('phone'); }
  };

  return (
    <div className="fixed inset-0 bg-black/55 z-[1000] flex items-center justify-center p-5">
      <div className="bg-white rounded-2xl w-full max-w-[420px] overflow-hidden shadow-2xl">
        <div className={`bg-gradient-to-br ${tier.bg} p-5`}>
          <p className="text-white/80 text-xs font-bold uppercase tracking-widest m-0">Subscribe</p>
          <p className="text-white text-xl font-black mt-1 mb-0">{tier.label} Plan</p>
          <p className="text-white text-2xl font-black mt-1.5 mb-0">KES {tier.price.toLocaleString()}<span className="text-sm font-medium opacity-80">/month</span></p>
        </div>

        <div className="p-6">
          {phase === 'phone' && (
            <>
              <div className="flex items-start gap-3 mb-5 bg-green-50 border border-green-200 rounded-xl p-3.5">
                <Smartphone className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800 m-0">Enter your M-Pesa number. You'll receive a push prompt to confirm payment.</p>
              </div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">M-Pesa Phone Number</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="0712 345 678"
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                className={`w-full px-4 py-3 text-base border-2 rounded-xl outline-none font-[inherit] ${error ? 'border-red-300' : 'border-gray-200 focus:border-blue-400'}`} />
              {error && <p className="text-red-500 text-xs mt-1.5">{error}</p>}
              <div className="flex gap-2.5 mt-5">
                <button onClick={onClose} className="flex-1 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-700 text-sm font-bold cursor-pointer hover:bg-gray-100">Cancel</button>
                <button onClick={handleSubmit} className="flex-[2] py-3 rounded-xl border-0 text-white text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity" style={{ background: tier.color }}>
                  Pay KES {tier.price.toLocaleString()}
                </button>
              </div>
            </>
          )}

          {phase === 'waiting' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: tier.soft, border: `2px solid ${tier.border}` }}>
                <Loader2 className="w-7 h-7 animate-spin" style={{ color: tier.color }} />
              </div>
              <p className="text-lg font-black text-gray-900 m-0">Check Your Phone</p>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">A payment prompt has been sent to <strong>{phone}</strong>.<br />Enter your M-Pesa PIN to complete.</p>
              <div className="flex items-center justify-center gap-2 mt-5 bg-amber-50 border border-amber-200 rounded-xl py-2.5 px-4">
                <Clock className="w-3.5 h-3.5 text-amber-600" />
                <p className="text-xs text-amber-800 m-0">Waiting for confirmation… (up to 2 minutes)</p>
              </div>
            </div>
          )}

          {phase === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <p className="text-lg font-black text-gray-900 m-0">Payment Successful!</p>
              <p className="text-sm text-gray-500 mt-2">Your account has been upgraded to <strong>{tier.label}</strong>.</p>
              {receipt && (
                <div className="flex items-center justify-center gap-2 mt-4 bg-emerald-50 border border-emerald-200 rounded-xl py-2.5 px-4">
                  <Receipt className="w-3.5 h-3.5 text-emerald-600" />
                  <p className="text-xs text-emerald-800 m-0">Receipt: <strong>{receipt}</strong></p>
                </div>
              )}
              <button onClick={onClose} className="mt-5 w-full py-3 rounded-xl border-0 bg-emerald-500 text-white text-sm font-bold cursor-pointer hover:bg-emerald-600 transition-colors">Continue to Dashboard</button>
            </div>
          )}

          {phase === 'failed' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-lg font-black text-gray-900 m-0">Payment Failed</p>
              <p className="text-sm text-gray-500 mt-2">{error}</p>
              <div className="flex gap-2.5 mt-5">
                <button onClick={onClose} className="flex-1 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-700 text-sm font-bold cursor-pointer">Close</button>
                <button onClick={() => { setPhase('phone'); setError(''); }} className="flex-[2] py-3 rounded-xl border-0 text-white text-sm font-bold cursor-pointer" style={{ background: tier.color }}>Try Again</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main SubscriptionPage ────────────────────────────────────────────────────
const SubscriptionPage = ({ userData, onUserUpdate }) => {
  const currentTier     = userData?.accessTier || 'basic';
  const assignedDevices = userData?.assignedDevices ?? [];
  const isBasicLocked   = currentTier === 'basic' && assignedDevices.length > 0;
  const needsBasicSetup = currentTier === 'basic' && assignedDevices.length === 0;

  const [activeTab,       setActiveTab]       = useState('plans');
  const [payingTier,      setPayingTier]      = useState(null);
  const [showBasicPicker, setShowBasicPicker] = useState(false);
  const [payments,        setPayments]        = useState([]);
  const [loadingHist,     setLoadingHist]     = useState(false);
  const [histError,       setHistError]       = useState('');

  useEffect(() => {
    setLoadingHist(true);
    getMyPayments().then(setPayments).catch(e => setHistError(e.message)).finally(() => setLoadingHist(false));
  }, []);

  const handleSuccess = (updatedUser) => {
    if (onUserUpdate) onUserUpdate(updatedUser);
    setPayingTier(null);
    setShowBasicPicker(false);
    getMyPayments().then(setPayments).catch(() => {});
  };

  const currentTierObj = TIERS.find(t => t.key === currentTier) || TIERS[0];
  const TierIcon = currentTierObj.icon;

  const initials = [userData?.firstName, userData?.lastName]
    .filter(Boolean).map(n => n[0].toUpperCase()).join('') || 'U';

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Account Hero ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">

            {/* User identity */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xl font-black shadow-lg flex-shrink-0">
                {initials}
              </div>
              <div>
                <h1 className="text-xl font-black text-gray-900 m-0">
                  {userData?.firstName} {userData?.lastName}
                </h1>
                <p className="text-sm text-gray-500 m-0">{userData?.email}</p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase tracking-wide">
                    <Shield className="w-2.5 h-2.5" />{userData?.role || 'user'}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide`}
                    style={{ background: currentTierObj.soft, color: currentTierObj.color, border: `1px solid ${currentTierObj.border}` }}>
                    <TierIcon className="w-2.5 h-2.5" />
                    {currentTierObj.label}
                    {currentTierObj.price === 0 ? ' · Free' : ` · KES ${currentTierObj.price.toLocaleString()}/mo`}
                  </span>
                </div>
              </div>
            </div>

            {/* Current plan highlight */}
            <div className="flex-shrink-0">
              <div className={`bg-gradient-to-br ${currentTierObj.bg} rounded-2xl px-5 py-3.5 text-white min-w-[180px]`}>
                <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest m-0">Active Plan</p>
                <p className="text-white text-lg font-black mt-0.5 mb-0">{currentTierObj.label}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
                  <span className="text-white/80 text-xs font-medium">
                    {currentTierObj.price === 0 ? 'Free forever' : `KES ${currentTierObj.price.toLocaleString()}/month`}
                  </span>
                </div>
                {isBasicLocked && (
                  <div className="mt-2 flex items-center gap-1.5 bg-white/20 rounded-lg px-2 py-1">
                    <Lock className="w-3 h-3 text-amber-300" />
                    <span className="text-[10px] font-bold text-amber-200">{assignedDevices.length} stations locked</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-5 border-b border-gray-200 -mb-px">
            {[
              { key: 'plans',   label: 'Plans',          icon: Star     },
              { key: 'billing', label: 'Billing History', icon: CreditCard },
            ].map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px ${
                  activeTab === key
                    ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                } rounded-t-lg`}>
                <Icon className="w-3.5 h-3.5" />
                {label}
                {key === 'billing' && payments.length > 0 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">{payments.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-7">

        {/* ── Plans Tab ──────────────────────────────────────────────────────── */}
        {activeTab === 'plans' && (
          <>
            {/* Upgrade prompt if on basic */}
            {currentTier === 'basic' && (
              <div className="mb-6 bg-gradient-to-r from-blue-600 to-violet-600 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Star className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm m-0">Unlock more stations & features</p>
                    <p className="text-white/70 text-xs mt-0.5 m-0">Upgrade to access historical data, exports, and API access.</p>
                  </div>
                </div>
                <button onClick={() => setActiveTab('plans')} className="flex items-center gap-2 bg-white text-blue-700 text-sm font-bold px-4 py-2 rounded-xl hover:bg-blue-50 transition-colors flex-shrink-0">
                  View Plans <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Tier grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {TIERS.map(tier => {
                const isActive = tier.key === currentTier;
                const TierIcon = tier.icon;
                return (
                  <div key={tier.key} className={`bg-white rounded-2xl flex flex-col overflow-hidden transition-all ${
                    isActive ? 'ring-2 shadow-xl' : 'border border-gray-200 hover:shadow-md hover:-translate-y-0.5'
                  }`} style={{ ...(isActive ? { ringColor: tier.color, boxShadow: `0 0 0 2px ${tier.color}, 0 16px 40px ${tier.color}22` } : {}) }}>

                    {/* Gradient header */}
                    <div className={`bg-gradient-to-br ${tier.bg} p-5 relative`}>
                      {tier.popular && !isActive && (
                        <div className="absolute top-3 right-3 bg-white/25 backdrop-blur-sm text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest">Popular</div>
                      )}
                      {isActive && (
                        <div className="absolute top-3 right-3 bg-white/25 backdrop-blur-sm text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest flex items-center gap-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" /> Current
                        </div>
                      )}
                      <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                        <TierIcon className="w-4.5 h-4.5 text-white" style={{ width: 18, height: 18 }} />
                      </div>
                      <p className="text-white font-black text-base m-0">{tier.label}</p>
                      <p className="text-white/70 text-[10px] m-0 mt-0.5">{tier.badge}</p>
                    </div>

                    <div className="p-5 flex-1 flex flex-col">
                      {/* Price */}
                      <div className="mb-4">
                        <span className="text-2xl font-black" style={{ color: tier.color }}>
                          {tier.price === 0 ? 'Free' : `KES ${tier.price.toLocaleString()}`}
                        </span>
                        {tier.price > 0 && <span className="text-xs text-gray-400 ml-1">/mo</span>}
                      </div>

                      {/* Features */}
                      <ul className="list-none p-0 m-0 flex-1 space-y-2 mb-5">
                        {tier.features.map(f => (
                          <li key={f} className="flex items-start gap-2">
                            <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: tier.soft }}>
                              <Check className="w-2.5 h-2.5" style={{ color: tier.color }} />
                            </div>
                            <span className="text-xs text-gray-600 leading-relaxed">{f}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      {isActive && tier.key === 'basic' && isBasicLocked ? (
                        <div className="space-y-2">
                          <div className="py-2 rounded-xl text-center text-xs font-bold" style={{ background: tier.soft, color: tier.color }}>Active Plan</div>
                          <div className="flex items-center gap-1.5 justify-center bg-amber-50 border border-amber-200 rounded-xl py-1.5 px-3">
                            <Lock className="w-3 h-3 text-amber-600" />
                            <span className="text-xs text-amber-800 font-bold">{assignedDevices.length} station{assignedDevices.length !== 1 ? 's' : ''} locked</span>
                          </div>
                        </div>
                      ) : isActive && tier.key === 'basic' && needsBasicSetup ? (
                        <button onClick={() => setShowBasicPicker(true)}
                          className="w-full py-2.5 rounded-xl border-0 text-white text-sm font-bold cursor-pointer flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                          style={{ background: tier.color }}>
                          <MapPin className="w-3.5 h-3.5" /> Choose Stations
                        </button>
                      ) : isActive ? (
                        <div className="py-2.5 rounded-xl text-center text-xs font-bold" style={{ background: tier.soft, color: tier.color }}>✓ Active Plan</div>
                      ) : (
                        <button onClick={() => tier.key === 'basic' ? setShowBasicPicker(true) : setPayingTier(tier)}
                          className="w-full py-2.5 rounded-xl border-0 text-white text-sm font-bold cursor-pointer flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity"
                          style={{ background: tier.color }}>
                          {tier.price === 0 ? 'Select Free Plan' : 'Subscribe'}
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Info note */}
            <div className="mt-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 m-0">
                After payment, station access is activated automatically for Research & Enterprise plans. Education plans require admin assignment of your specific school station.
              </p>
            </div>
          </>
        )}

        {/* ── Billing Tab ────────────────────────────────────────────────────── */}
        {activeTab === 'billing' && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Receipt className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 m-0">Payment History</p>
                  <p className="text-xs text-gray-400 m-0">{payments.length} transaction{payments.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button onClick={() => { setLoadingHist(true); getMyPayments().then(setPayments).catch(e => setHistError(e.message)).finally(() => setLoadingHist(false)); }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors border-0 bg-transparent cursor-pointer">
                <RefreshCw className={`w-4 h-4 text-gray-400 ${loadingHist ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {loadingHist ? (
              <div className="py-16 text-center"><Loader2 className="w-6 h-6 text-gray-400 animate-spin mx-auto" /></div>
            ) : histError ? (
              <div className="py-12 text-center">
                <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-sm text-red-500">{histError}</p>
              </div>
            ) : payments.length === 0 ? (
              <div className="py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-7 h-7 text-gray-400" />
                </div>
                <p className="text-sm font-bold text-gray-500 m-0">No payments yet</p>
                <p className="text-xs text-gray-400 mt-1">Your payment history will appear here after subscribing.</p>
                <button onClick={() => setActiveTab('plans')}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl cursor-pointer border-0 hover:bg-blue-700 transition-colors">
                  View Plans <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {['Date', 'Plan', 'Amount', 'Receipt', 'Status'].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => {
                      const t = TIERS.find(x => x.key === p.tier) || TIERS[0];
                      const statusColor = p.status === 'completed' ? '#10b981' : p.status === 'pending' ? '#f59e0b' : '#ef4444';
                      const statusBg    = p.status === 'completed' ? '#f0fdf4' : p.status === 'pending' ? '#fffbeb' : '#fef2f2';
                      return (
                        <tr key={p._id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                          <td className="px-5 py-3.5 text-xs text-gray-600">{new Date(p.createdAt).toLocaleDateString('en-KE', { dateStyle: 'medium' })}</td>
                          <td className="px-5 py-3.5">
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: t.soft, color: t.color }}>
                              <t.icon className="w-3 h-3" /> {t.label}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-sm font-black text-gray-900">KES {p.amount.toLocaleString()}</td>
                          <td className="px-5 py-3.5 text-xs text-gray-500 font-mono">{p.mpesaReceiptNumber || '—'}</td>
                          <td className="px-5 py-3.5">
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide" style={{ background: statusBg, color: statusColor }}>
                              {p.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                              {p.status === 'pending'   && <Clock className="w-3 h-3" />}
                              {p.status === 'failed'    && <XCircle className="w-3 h-3" />}
                              {p.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {showBasicPicker && <BasicSetupModal onClose={() => setShowBasicPicker(false)} onSuccess={handleSuccess} />}
      {payingTier && <PaymentModal tier={payingTier} onClose={() => setPayingTier(null)} onSuccess={handleSuccess} />}
    </div>
  );
};

export default SubscriptionPage;
